import type { InvoicePart } from '../types/invoice';
import type { UnmatchedLineItemsNexSubmitMode } from './unmatchedLineItemsNexSubmitMode';
import {
    allowsManualUnmatchedNexSubmit,
    normalizeUnmatchedLineItemsNexSubmitMode,
} from './unmatchedLineItemsNexSubmitMode';
import { isSalesTaxLineItemPart } from './salesTaxAsLineItem';

export interface NexLineMatchSubmitInputs {
    lineItems: InvoicePart[];
    freightItems?: InvoicePart[];
    fuelItems?: InvoicePart[];
    matchedPartIndices: Set<number>;
    unmatchedPartIndices: Set<number>;
    unmatchedLineItemsNexSubmitMode?: UnmatchedLineItemsNexSubmitMode | unknown;
    /** @deprecated prefer unmatchedLineItemsNexSubmitMode */
    allowUnmatchedLineItemsOnNexSubmit?: boolean;
}

export interface NexLineMatchSubmitEvaluation {
    unmatchedBillablePartIndices: number[];
    blockSubmit: boolean;
    blockReason?: 'has_unmatched' | 'all_unmatched';
}

function countBillableRows(inputs: NexLineMatchSubmitInputs): number {
    const lineCount = (inputs.lineItems ?? []).filter((p) => !isSalesTaxLineItemPart(p)).length;
    return lineCount + (inputs.freightItems?.length ?? 0) + (inputs.fuelItems?.length ?? 0);
}

/** Mirrors API `evaluateNexLineMatchSubmitBlock` for invoice detail submit gating (manual path). */
export function evaluateNexLineMatchSubmitForUi(inputs: NexLineMatchSubmitInputs): NexLineMatchSubmitEvaluation {
    const { lineItems, freightItems = [], fuelItems = [], matchedPartIndices, unmatchedPartIndices } = inputs;
    const unmatchedBillablePartIndices: number[] = [];

    lineItems.forEach((p, idx) => {
        if (isSalesTaxLineItemPart(p)) return;
        if (matchedPartIndices.has(idx) && !unmatchedPartIndices.has(idx)) return;
        unmatchedBillablePartIndices.push(idx);
    });

    const freightOffset = lineItems.length;
    freightItems.forEach((_, idx) => {
        const globalIdx = freightOffset + idx;
        if (matchedPartIndices.has(globalIdx) && !unmatchedPartIndices.has(globalIdx)) return;
        unmatchedBillablePartIndices.push(globalIdx);
    });

    const fuelOffset = freightOffset + freightItems.length;
    fuelItems.forEach((_, idx) => {
        const globalIdx = fuelOffset + idx;
        if (matchedPartIndices.has(globalIdx) && !unmatchedPartIndices.has(globalIdx)) return;
        unmatchedBillablePartIndices.push(globalIdx);
    });

    const totalBillable = countBillableRows(inputs);
    const matchedBillableCount = totalBillable - unmatchedBillablePartIndices.length;
    const mode = normalizeUnmatchedLineItemsNexSubmitMode(
        inputs.unmatchedLineItemsNexSubmitMode ?? inputs.allowUnmatchedLineItemsOnNexSubmit,
    );

    if (totalBillable > 0 && matchedBillableCount === 0) {
        return {
            unmatchedBillablePartIndices,
            blockSubmit: true,
            blockReason: 'all_unmatched',
        };
    }

    if (unmatchedBillablePartIndices.length === 0) {
        return {
            unmatchedBillablePartIndices,
            blockSubmit: false,
        };
    }

    if (!allowsManualUnmatchedNexSubmit(mode)) {
        return {
            unmatchedBillablePartIndices,
            blockSubmit: true,
            blockReason: 'has_unmatched',
        };
    }

    return {
        unmatchedBillablePartIndices,
        blockSubmit: false,
    };
}
